import { Role } from '@bioliberty/core';
import { User } from '@bioliberty/lifemind';
import { ColumnDef } from '@tanstack/react-table';
import { useAuthentication } from '../../auth';
import { SortableHeader } from '../../components/DataTable/SortableHeader';
import { NoCapture } from '../../components/NoCapture';
import { isSuperAdmin } from '../../util/auth';
import { UserActions } from '../components/UserActions';
import { UserStatusCell } from '../components/UserStatusCell';
import { getRoleLabel, getUserStatusBadgeProps } from '../utils';

export function useUsersTableColumns() {
  const currentUser = useAuthentication();

  const columnDefinitions: ColumnDef<User>[] = [
    {
      accessorKey: 'email',
      header: ({ column }) => <SortableHeader column={column}>Email Address</SortableHeader>,
      cell: ({ row }) => <NoCapture>{row.getValue('email')}</NoCapture>,
      minSize: 600
    },
    {
      accessorKey: 'firstName',
      header: ({ column }) => <SortableHeader column={column}>First Name</SortableHeader>,
      cell: ({ row }) => <NoCapture>{row.getValue('firstName')}</NoCapture>,
      minSize: 200
    },
    {
      accessorKey: 'lastName',
      header: ({ column }) => <SortableHeader column={column}>Last Name</SortableHeader>,
      cell: ({ row }) => <NoCapture>{row.getValue('lastName')}</NoCapture>,
      minSize: 200
    },
    {
      accessorKey: 'roles',
      header: ({ column }) => <SortableHeader column={column}>Role</SortableHeader>,
      cell: ({ row }) => {
        const roles: string[] = row.getValue('roles');
        const role = roles[0] as Role;
        return <span className="capitalize">{getRoleLabel(role)}</span>;
      },
      minSize: 300
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
      cell: ({ row }) => <UserStatusCell user={row.original} />,
      sortingFn: ({ original: userA }, { original: userB }) => {
        const badgeA = getUserStatusBadgeProps(userA);
        const badgeB = getUserStatusBadgeProps(userB);
        return badgeA.label.localeCompare(badgeB.label);
      },
      minSize: 200
    },
    {
      accessorKey: 'id',
      header: '',
      size: 100,
      cell: ({ row }) => <UserActions {...row.original} />
    }
  ];

  if (currentUser.subtenants?.length) {
    columnDefinitions.splice(4, 0, {
      accessorKey: 'subtenants',
      header: ({ column }) => <SortableHeader column={column}>Clinics</SortableHeader>,
      cell: ({ row }) => {
        const subtenantNames = row.getValue('subtenants') as string[] | undefined;

        const subtenantLabels = currentUser.subtenants
          ?.filter(({ name }) => isSuperAdmin(row.original) || subtenantNames?.includes(name))
          ?.map(({ label }) => label)
          ?.join(', ');

        return (
          <div title={subtenantLabels} className="max-w-[200px] overflow-hidden overflow-ellipsis whitespace-nowrap">
            {subtenantLabels}
          </div>
        );
      },
      minSize: 300
    });
  }

  return columnDefinitions;
}
